<!--
  SAX Properties Panel - Property Editor

  FOR: pricing-portal-laravel/resources/js/components/sites/documentation-tabs/sax/SaxPropertiesPanel.vue

  Edits properties of selected components or links
  Supports various input types with validation
-->

<template>
  <div class="sax-properties-panel">
    <!-- Header -->
    <div class="panel-header">
      <div class="text-subtitle2 text-grey-9 q-pa-sm">
        <q-icon name="settings" class="q-mr-xs" />
        Properties
      </div>
    </div>

    <!-- Content -->
    <div class="panel-content">
      <!-- No Selection -->
      <div v-if="selectedItems.length === 0" class="no-selection q-pa-md text-center">
        <q-icon name="touch_app" size="4em" color="grey-5" />
        <div class="text-caption text-grey-7 q-mt-sm">
          Select a component or link to edit properties
        </div>
      </div>

      <!-- Multiple Selection -->
      <div v-else-if="selectedItems.length > 1" class="multi-selection q-pa-md">
        <q-banner class="bg-blue-1" rounded>
          <template #avatar>
            <q-icon name="info" color="primary" />
          </template>
          {{ selectedItems.length }} items selected
        </q-banner>

        <div class="q-mt-md">
          <q-btn
            label="Delete Selected"
            icon="delete"
            color="negative"
            outline
            class="full-width"
            :disable="readonly"
            @click="deleteSelected"
          />
        </div>
      </div>

      <!-- Single Component -->
      <div v-else-if="currentItem && currentItem.type === 'component'" class="component-properties">
        <q-form class="q-pa-md q-gutter-md">
          <!-- Component Name -->
          <div class="property-section">
            <q-input
              :model-value="currentItem.data.name"
              label="Name"
              dense
              outlined
              :readonly="readonly"
              @update:model-value="updateProperty('name', $event)"
            >
              <template #prepend>
                <q-icon name="label" color="primary" size="xs" />
              </template>
            </q-input>
          </div>

          <!-- Kit Name -->
          <div class="property-section">
            <q-input
              :model-value="currentItem.data.kit || currentItem.data.kit_name"
              label="Kit"
              dense
              outlined
              readonly
              disable
            />
          </div>

          <!-- Input Slots Section -->
          <div v-if="currentItem.data.input_slots?.length > 0" class="property-section">
            <div class="section-header text-caption text-weight-bold text-grey-7 q-mb-sm">
              <q-icon name="input" class="q-mr-xs" />
              Input Slots
            </div>

            <div
              v-for="(slot, index) in currentItem.data.input_slots"
              :key="`input_${index}`"
              class="q-mb-sm"
            >
              <!-- Config slot (editable) - Boolean -->
              <q-toggle
                v-if="isConfigSlot(slot) && slot.type === 'bool'"
                :model-value="getConfigValue(slot.name, slot.default)"
                :label="slot.name"
                :disable="readonly"
                @update:model-value="updateConfigValue(slot.name, $event)"
              >
                <q-tooltip v-if="slot.description">{{ slot.description }}</q-tooltip>
              </q-toggle>

              <!-- Config slot (editable) - Number -->
              <q-input
                v-else-if="isConfigSlot(slot) && (slot.type === 'int' || slot.type === 'float')"
                :model-value="getConfigValue(slot.name, slot.default)"
                :label="slot.name"
                :type="slot.type === 'int' ? 'number' : 'text'"
                :step="slot.type === 'int' ? '1' : '0.01'"
                dense
                outlined
                :readonly="readonly"
                @update:model-value="updateConfigValue(slot.name, $event)"
              >
                <template #prepend>
                  <q-icon name="tune" color="primary" size="xs" />
                </template>
                <template #append>
                  <q-icon v-if="slot.description" name="info" color="grey-6">
                    <q-tooltip>{{ slot.description }}</q-tooltip>
                  </q-icon>
                </template>
              </q-input>

              <!-- Config slot (editable) - Enum/Select -->
              <q-select
                v-else-if="isConfigSlot(slot) && slot.values && slot.values.length > 0"
                :model-value="getConfigValue(slot.name, slot.default)"
                :label="slot.name"
                :options="slot.values"
                dense
                outlined
                emit-value
                map-options
                :readonly="readonly"
                @update:model-value="updateConfigValue(slot.name, $event)"
              >
                <template #prepend>
                  <q-icon name="tune" color="primary" size="xs" />
                </template>
                <template #append>
                  <q-icon v-if="slot.description" name="info" color="grey-6">
                    <q-tooltip>{{ slot.description }}</q-tooltip>
                  </q-icon>
                </template>
              </q-select>

              <!-- Config slot (editable) - String -->
              <q-input
                v-else-if="isConfigSlot(slot)"
                :model-value="getConfigValue(slot.name, slot.default)"
                :label="slot.name"
                type="text"
                dense
                outlined
                :readonly="readonly"
                @update:model-value="updateConfigValue(slot.name, $event)"
              >
                <template #prepend>
                  <q-icon name="tune" color="primary" size="xs" />
                </template>
                <template #append>
                  <q-icon v-if="slot.description" name="info" color="grey-6">
                    <q-tooltip>{{ slot.description }}</q-tooltip>
                  </q-icon>
                </template>
              </q-input>

              <!-- Non-config slot (read-only, shows connection status) -->
              <q-input
                v-else
                :model-value="'Connected'"
                :label="slot.name"
                :hint="`Type: ${slot.type || 'any'}`"
                dense
                outlined
                readonly
                disable
              >
                <template #prepend>
                  <q-icon name="cable" color="green" size="xs" />
                </template>
              </q-input>
            </div>
          </div>

          <!-- Output Slots Section -->
          <div v-if="currentItem.data.output_slots?.length > 0" class="property-section">
            <div class="section-header text-caption text-weight-bold text-grey-7 q-mb-sm">
              <q-icon name="output" class="q-mr-xs" />
              Output Slots
            </div>

            <div
              v-for="(slot, index) in currentItem.data.output_slots"
              :key="`output_${index}`"
              class="q-mb-sm"
            >
              <!-- Config slot (editable) - Boolean -->
              <q-toggle
                v-if="isConfigSlot(slot) && slot.type === 'bool'"
                :model-value="getConfigValue(slot.name, slot.default)"
                :label="slot.name"
                :disable="readonly"
                @update:model-value="updateConfigValue(slot.name, $event)"
              >
                <q-tooltip v-if="slot.description">{{ slot.description }}</q-tooltip>
              </q-toggle>

              <!-- Config slot (editable) - Number -->
              <q-input
                v-else-if="isConfigSlot(slot) && (slot.type === 'int' || slot.type === 'float')"
                :model-value="getConfigValue(slot.name, slot.default)"
                :label="slot.name"
                :type="slot.type === 'int' ? 'number' : 'text'"
                :step="slot.type === 'int' ? '1' : '0.01'"
                dense
                outlined
                :readonly="readonly"
                @update:model-value="updateConfigValue(slot.name, $event)"
              >
                <template #prepend>
                  <q-icon name="tune" color="primary" size="xs" />
                </template>
                <template #append>
                  <q-icon v-if="slot.description" name="info" color="grey-6">
                    <q-tooltip>{{ slot.description }}</q-tooltip>
                  </q-icon>
                </template>
              </q-input>

              <!-- Config slot (editable) - Enum/Select -->
              <q-select
                v-else-if="isConfigSlot(slot) && slot.values && slot.values.length > 0"
                :model-value="getConfigValue(slot.name, slot.default)"
                :label="slot.name"
                :options="slot.values"
                dense
                outlined
                emit-value
                map-options
                :readonly="readonly"
                @update:model-value="updateConfigValue(slot.name, $event)"
              >
                <template #prepend>
                  <q-icon name="tune" color="primary" size="xs" />
                </template>
                <template #append>
                  <q-icon v-if="slot.description" name="info" color="grey-6">
                    <q-tooltip>{{ slot.description }}</q-tooltip>
                  </q-icon>
                </template>
              </q-select>

              <!-- Config slot (editable) - String -->
              <q-input
                v-else-if="isConfigSlot(slot)"
                :model-value="getConfigValue(slot.name, slot.default)"
                :label="slot.name"
                type="text"
                dense
                outlined
                :readonly="readonly"
                @update:model-value="updateConfigValue(slot.name, $event)"
              >
                <template #prepend>
                  <q-icon name="tune" color="primary" size="xs" />
                </template>
                <template #append>
                  <q-icon v-if="slot.description" name="info" color="grey-6">
                    <q-tooltip>{{ slot.description }}</q-tooltip>
                  </q-icon>
                </template>
              </q-input>

              <!-- Non-config slot (read-only, shows connection status) -->
              <q-input
                v-else
                :model-value="'Connected'"
                :label="slot.name"
                :hint="`Type: ${slot.type || 'any'}`"
                dense
                outlined
                readonly
                disable
              >
                <template #prepend>
                  <q-icon name="cable" color="orange" size="xs" />
                </template>
              </q-input>
            </div>
          </div>

          <!-- Custom Properties Section -->
          <div v-if="componentProperties.length > 0" class="property-section">
            <div class="section-header text-caption text-weight-bold text-grey-7 q-mb-sm">
              Properties
            </div>

            <div
              v-for="prop in componentProperties"
              :key="prop.key"
              class="property-field"
            >
              <!-- Text Input -->
              <q-input
                v-if="prop.type === 'text' || prop.type === 'string'"
                :model-value="prop.value"
                :label="prop.label"
                dense
                outlined
                :readonly="readonly"
                @update:model-value="updateCustomProperty(prop.key, $event)"
              />

              <!-- Number Input -->
              <q-input
                v-else-if="prop.type === 'number'"
                :model-value="prop.value"
                :label="prop.label"
                type="number"
                dense
                outlined
                :readonly="readonly"
                @update:model-value="updateCustomProperty(prop.key, parseFloat($event))"
              />

              <!-- Boolean Checkbox -->
              <q-checkbox
                v-else-if="prop.type === 'boolean'"
                :model-value="prop.value"
                :label="prop.label"
                :disable="readonly"
                @update:model-value="updateCustomProperty(prop.key, $event)"
              />

              <!-- Select Dropdown -->
              <q-select
                v-else-if="prop.type === 'select'"
                :model-value="prop.value"
                :label="prop.label"
                :options="prop.options"
                dense
                outlined
                :readonly="readonly"
                @update:model-value="updateCustomProperty(prop.key, $event)"
              />

              <!-- Default (text) -->
              <q-input
                v-else
                :model-value="prop.value"
                :label="prop.label"
                dense
                outlined
                :readonly="readonly"
                @update:model-value="updateCustomProperty(prop.key, $event)"
              />
            </div>
          </div>

          <!-- Actions -->
          <div class="property-section">
            <q-btn
              label="Delete Component"
              icon="delete"
              color="negative"
              outline
              class="full-width"
              :disable="readonly"
              @click="deleteComponent"
            />
          </div>
        </q-form>
      </div>

      <!-- Single Link -->
      <div v-else-if="currentItem && currentItem.type === 'link'" class="link-properties">
        <q-form class="q-pa-md q-gutter-md">
          <!-- Link Info Section -->
          <div class="property-section">
            <div class="section-header text-caption text-weight-bold text-grey-7 q-mb-sm">
              Link Information
            </div>

            <!-- Link ID -->
            <q-input
              :model-value="currentItem.data.id"
              label="ID"
              dense
              outlined
              readonly
              disable
            >
              <template #append>
                <q-btn
                  icon="content_copy"
                  flat
                  dense
                  size="sm"
                  @click="copyToClipboard(currentItem.data.id)"
                >
                  <q-tooltip>Copy ID</q-tooltip>
                </q-btn>
              </template>
            </q-input>

            <!-- From Component -->
            <q-input
              :model-value="getLinkComponentName(currentItem.data.from_component)"
              label="From Component"
              dense
              outlined
              readonly
            >
              <template #append>
                <q-icon name="arrow_forward" />
              </template>
            </q-input>

            <!-- From Slot -->
            <q-input
              :model-value="currentItem.data.from_slot"
              label="From Slot"
              dense
              outlined
              readonly
            >
              <template #prepend>
                <q-icon name="output" color="orange" />
              </template>
            </q-input>

            <!-- To Component -->
            <q-input
              :model-value="getLinkComponentName(currentItem.data.to_component)"
              label="To Component"
              dense
              outlined
              readonly
            >
              <template #append>
                <q-icon name="arrow_forward" />
              </template>
            </q-input>

            <!-- To Slot -->
            <q-input
              :model-value="currentItem.data.to_slot"
              label="To Slot"
              dense
              outlined
              readonly
            >
              <template #prepend>
                <q-icon name="input" color="green" />
              </template>
            </q-input>
          </div>

          <!-- Actions -->
          <div class="property-section">
            <q-btn
              label="Delete Link"
              icon="delete"
              color="negative"
              outline
              class="full-width"
              :disable="readonly"
              @click="deleteLink"
            />
          </div>
        </q-form>
      </div>
    </div>

    <!-- Color Picker Dialog -->
    <q-dialog v-model="showColorPicker">
      <q-card>
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Choose Color</div>
          <q-space />
          <q-btn v-close-popup icon="close" flat round dense />
        </q-card-section>

        <q-card-section>
          <q-color
            :model-value="currentItem?.data?.color || '#3B82F6'"
            @update:model-value="updateProperty('color', $event)"
          />
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup label="Done" color="primary" flat />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
  import { ref, computed, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import { useSaxEditorStore } from '@/stores/saxEditor';
  import { storeToRefs } from 'pinia';

  // Props
  const props = defineProps({
    selectedItems: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  });

  // Emits
  const emit = defineEmits(['property-changed']);

  // Quasar
  const $q = useQuasar();

  // Store
  const saxStore = useSaxEditorStore();
  const { currentSaxData } = storeToRefs(saxStore);

  // State
  const showColorPicker = ref(false);

  // Computed

  /**
   * Current single selected item
   */
  const currentItem = computed(() => {
    const item = props.selectedItems.length === 1 ? props.selectedItems[0] : null;
    return item;
  });

  /**
   * Component custom properties
   */
  const componentProperties = computed(() => {
    if (!currentItem.value || currentItem.value.type !== 'component') {
      return [];
    }

    const properties = currentItem.value.data.properties || {};
    return Object.entries(properties).map(([key, value]) => {
      return {
        key,
        label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        value,
        type: inferPropertyType(value),
      };
    });
  });

  // Methods

  /**
   * Check if slot is a config slot (has 'c' flag)
   */
  function isConfigSlot(slot) {
    return slot.flags && slot.flags.includes('c');
  }

  /**
   * Infer property type from value
   */
  function inferPropertyType(value) {
    if (typeof value === 'boolean') {return 'boolean';}
    if (typeof value === 'number') {return 'number';}
    if (typeof value === 'string') {return 'text';}
    return 'text';
  }

  /**
   * Update property
   */
  function updateProperty(property, value) {
    if (props.readonly) {return;}

    emit('property-changed', {
      itemId: currentItem.value.id,
      itemType: currentItem.value.type,
      property,
      value,
    });
  }

  /**
   * Update position
   */
  function updatePosition(axis, value) {
    if (props.readonly) {return;}

    const newPosition = {
      ...currentItem.value.data.position,
      [axis]: value,
    };

    updateProperty('position', newPosition);
  }

  /**
   * Update custom property
   */
  function updateCustomProperty(key, value) {
    if (props.readonly) {return;}

    const newProperties = {
      ...(currentItem.value.data.properties || {}),
      [key]: value,
    };

    updateProperty('properties', newProperties);
  }

  /**
   * Get configuration value for a slot
   */
  function getConfigValue(slotName, defaultValue) {
    if (!currentItem.value || currentItem.value.type !== 'component') {
      return defaultValue;
    }

    const configValues = currentItem.value.data.config_values || {};
    return configValues[slotName] !== undefined ? configValues[slotName] : defaultValue;
  }

  /**
   * Update configuration value for a slot
   */
  function updateConfigValue(slotName, value) {
    if (props.readonly) {return;}

    const newConfigValues = {
      ...(currentItem.value.data.config_values || {}),
      [slotName]: value,
    };

    updateProperty('config_values', newConfigValues);
  }

  /**
   * Get component name by ID
   */
  function getLinkComponentName(componentId) {
    const component = currentSaxData.value?.components?.find((c) => c.id === componentId);
    return component ? `${component.name} (${component.type})` : componentId;
  }

  /**
   * Delete component
   */
  function deleteComponent() {
    $q.dialog({
      title: 'Delete Component',
      message: 'Are you sure you want to delete this component? All connected links will also be deleted.',
      cancel: true,
      persistent: true,
    }).onOk(() => {
      saxStore.deleteComponent(currentItem.value.id);
      $q.notify({
        type: 'positive',
        message: 'Component deleted',
        timeout: 1000,
      });
    });
  }

  /**
   * Delete link
   */
  function deleteLink() {
    $q.dialog({
      title: 'Delete Link',
      message: 'Are you sure you want to delete this link?',
      cancel: true,
      persistent: true,
    }).onOk(() => {
      saxStore.deleteLink(currentItem.value.id);
      $q.notify({
        type: 'positive',
        message: 'Link deleted',
        timeout: 1000,
      });
    });
  }

  /**
   * Delete selected items
   */
  function deleteSelected() {
    $q.dialog({
      title: 'Delete Items',
      message: `Are you sure you want to delete ${props.selectedItems.length} items?`,
      cancel: true,
      persistent: true,
    }).onOk(() => {
      props.selectedItems.forEach((item) => {
        if (item.type === 'component') {
          saxStore.deleteComponent(item.id);
        } else if (item.type === 'link') {
          saxStore.deleteLink(item.id);
        }
      });

      $q.notify({
        type: 'positive',
        message: `${props.selectedItems.length} items deleted`,
        timeout: 1000,
      });
    });
  }

  /**
   * Copy to clipboard
   */
  function copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(() => {
      $q.notify({
        type: 'positive',
        message: 'Copied to clipboard',
        timeout: 1000,
      });
    });
  }
</script>

<style scoped lang="scss">
.sax-properties-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: white;
}

.panel-header {
  flex: 0 0 auto;
  border-bottom: 1px solid #e0e0e0;
}

.panel-content {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  max-height: calc(100vh - 200px);
}

.no-selection {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.property-section {
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb; // Slightly darker for better definition

  &:last-child {
    border-bottom: none;
  }
}

.section-header {
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: #f5f5f5; // Add subtle background
  padding: 6px 8px; // Add padding
  border-radius: 4px; // Rounded corners
  margin-bottom: 12px; // More spacing
}

.color-preview {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #ccc;
}

.property-field {
  margin-bottom: 8px;
}
</style>
